import { supabase } from '../lib/supabase';

// Helper function to get current user - supports both Supabase Auth and test users
const getCurrentUser = async (providedUser = null) => {
  // If user is provided directly, use it
  if (providedUser) {
    return providedUser;
  }

  // Otherwise, try to get from Supabase Auth
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Get user preference by key
export const getUserPreference = async (preferenceKey, defaultValue = null, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in getUserPreference');
      return { data: defaultValue, error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('user_preferences')
      .select('preference_value')
      .eq('user_id', user.id)
      .eq('preference_key', preferenceKey)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No row found, return default value
        return { data: defaultValue, error: null };
      }
      // Handle 406 errors specifically - EXPECTED BEHAVIOR
      // 406 (Not Acceptable) errors occur when Row Level Security (RLS) policies
      // prevent access to other users' preferences. This is intentional security.
      if (error.status === 406) {
        return { data: defaultValue, error: null };
      }
      console.error('Error fetching user preference:', error);
      return { data: defaultValue, error };
    }

    return { data: data.preference_value, error: null };
  } catch (error) {
    console.error('Exception in getUserPreference:', error);
    return { data: defaultValue, error };
  }
};

// Set user preference
export const setUserPreference = async (preferenceKey, preferenceValue, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in setUserPreference');
      return { data: null, error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('user_preferences')
      .upsert({
        user_id: user.id,
        preference_key: preferenceKey,
        preference_value: preferenceValue,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,preference_key'
      })
      .select();

    if (error) {
      // Handle specific error types
      if (error.code === '23505') {
        // Unique constraint violation - this shouldn't happen with upsert, but handle it
        console.warn('Unique constraint violation in user preferences, retrying...');
        return { data: null, error };
      }
      if (error.status === 409) {
        // Conflict error - might be a race condition
        console.warn('Conflict setting user preference, this may be a race condition');
        return { data: null, error };
      }
      console.error('Error setting user preference:', error);
      return { data: null, error };
    }

    return { data: data[0], error: null };
  } catch (error) {
    console.error('Exception in setUserPreference:', error);
    return { data: null, error };
  }
};

// Get multiple user preferences at once
export const getUserPreferences = async (preferenceKeys = [], currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in getUserPreferences');
      return { data: {}, error: 'User not authenticated' };
    }

    let query = supabase
      .from('user_preferences')
      .select('preference_key, preference_value')
      .eq('user_id', user.id);

    // Filter by specific keys if provided
    if (preferenceKeys.length > 0) {
      query = query.in('preference_key', preferenceKeys);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user preferences:', error);
      return { data: {}, error };
    }

    // Convert to key-value object
    const preferences = {};
    data?.forEach(row => {
      preferences[row.preference_key] = row.preference_value;
    });

    return { data: preferences, error: null };
  } catch (error) {
    console.error('Exception in getUserPreferences:', error);
    return { data: {}, error };
  }
};

// Delete user preference
export const deleteUserPreference = async (preferenceKey, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in deleteUserPreference');
      return { data: null, error: 'User not authenticated' };
    }

    const { error } = await supabase
      .from('user_preferences')
      .delete()
      .eq('user_id', user.id)
      .eq('preference_key', preferenceKey);

    if (error) {
      console.error('Error deleting user preference:', error);
      return { data: null, error };
    }

    return { data: true, error: null };
  } catch (error) {
    console.error('Exception in deleteUserPreference:', error);
    return { data: null, error };
  }
};

// Clear all user preferences
export const clearUserPreferences = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in clearUserPreferences');
      return { data: null, error: 'User not authenticated' };
    }

    const { error } = await supabase
      .from('user_preferences')
      .delete()
      .eq('user_id', user.id);

    if (error) {
      console.error('Error clearing user preferences:', error);
      return { data: null, error };
    }

    return { data: true, error: null };
  } catch (error) {
    console.error('Exception in clearUserPreferences:', error);
    return { data: null, error };
  }
};

// Availability-specific preference helpers
export const getAvailabilityPreference = async (currentUser = null) => {
  const { data } = await getUserPreference('availability_preference', 'available', currentUser);
  return data;
};

export const setAvailabilityPreference = async (preference, currentUser = null) => {
  return await setUserPreference('availability_preference', preference, currentUser);
};

// Week-start preference: '0' = Sunday, '1' = Monday. Defaults to Monday for
// the UK / international convention.
export const getWeekStartDay = async (currentUser = null) => {
  const { data } = await getUserPreference('week_start_day', '1', currentUser);
  // Stored as a string in user_preferences; coerce to a 0/1 number for callers.
  const n = Number(data);
  return n === 0 ? 0 : 1;
};

export const setWeekStartDay = async (value, currentUser = null) => {
  // Accept 0/1 or '0'/'1'; store as string to match the table column type.
  return await setUserPreference('week_start_day', String(value), currentUser);
};

// Reduce-motion preference: when true, disables transitions and framer-motion
// animations across the app. Stored as a string ('true' / 'false') to match the
// existing user_preferences column type.
export const getReduceMotion = async (currentUser = null) => {
  const { data } = await getUserPreference('reduce_motion', 'false', currentUser);
  return data === 'true';
};

export const setReduceMotion = async (enabled, currentUser = null) => {
  return await setUserPreference('reduce_motion', enabled ? 'true' : 'false', currentUser);
};

// Onboarding completion has moved to public.user_profiles.onboarding_completed
// (boolean). Import from `userProfileService` instead. The migration that
// introduced the new table also backfills + deletes the legacy preference rows.

// Enhanced function to get all user preferences
export const getAllUserPreferences = async (currentUser = null) => {
  return await getUserPreferences(
    ['availability_preference', 'week_start_day', 'reduce_motion'],
    currentUser
  );
};